import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dropdown } from 'src/components/common/dropdown';
import { useAssignmentsFilteringSubjects } from 'src/hooks/use-assignments-filtering-subjects';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { usePrevious } from 'src/hooks/use-previous.hook';
import { useUserAssets } from 'src/hooks/use-user-assets.hook';
import { XApiEventKeys } from 'src/interfaces/event.types';
import { getAmpConfigFlags } from 'src/services/amp-config.service';
import { hasTxAsset } from 'src/services/entitlement.service';
import eventService from 'src/services/event.service';
import {
  setActiveMSAssignmentsSubject,
  useSelectedClassSelector,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';

import './subjects-filter.scss';

const ALL_SUBJECTS = 'all-subjects';

export const SubjectsFilter = () => {
  const { t } = useTranslation();
  const { data: userAssets } = useUserAssets();

  const [initialSelectedSubject, setInitialSelectedSubject] = useState([
    ALL_SUBJECTS,
  ]);

  const selectedClass = useSelectedClassSelector();
  const selectedClassKey = selectedClass?.length
    ? selectedClass.map((cl) => cl.sourcedId)
    : [];
  const prevSelectedClassKey = usePrevious(selectedClassKey);

  const dispatch = useAppDispatch();

  const commonXapiContextPayload = useCommonXapiContextPayloadHook();
  const assignmentsSubjects = useAssignmentsFilteringSubjects();
  const assignmentsSubjectsCount = assignmentsSubjects?.length ?? 0;

  // TODO: Remove the feature flag after RFS.
  const isUpdatedMSViewEnabled = getAmpConfigFlags().isUpdatedMSViewEnabled;

  const handleSelect = (subject: string, _: string, eventType?: string) => {
    if (!assignmentsSubjectsCount) {
      return;
    }

    // We need this to find a assignments that have ELA subject that was renamed to ELAR for TX users
    if (hasTxAsset(userAssets) && subject === 'ELAR') {
      dispatch(setActiveMSAssignmentsSubject('ELA'));
    } else if (eventType !== 'contextmenu') {
      dispatch(
        setActiveMSAssignmentsSubject(
          subject === ALL_SUBJECTS ? null : subject,
        ),
      );
    }

    eventService.send(
      XApiEventKeys.selectedSubjectFilter,
      {
        result: {
          productFiltersSelected: subject,
        },
      },
      {
        result: {
          isRightClick: eventType === 'contextmenu',
          ...commonXapiContextPayload,
        },
      },
    );
  };

  useEffect(() => {
    const hasClassChanged =
      !!prevSelectedClassKey?.length &&
      selectedClassKey.join('') !== prevSelectedClassKey.join('');

    if (hasClassChanged) {
      setInitialSelectedSubject([ALL_SUBJECTS]);
      dispatch(setActiveMSAssignmentsSubject(null));
    }
    // Avoid including prevSelectedClassKey in deps as we only track the active class update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassKey, dispatch]);

  return (
    <Dropdown
      classNames={{
        button: clsx('subject-filter__button', {
          'subject-filter__button--updated': isUpdatedMSViewEnabled,
        }),
        container: clsx('subject-filter', {
          'subject-filter--updated': isUpdatedMSViewEnabled,
        }),
      }}
      onSelect={handleSelect}
      isLoading={false}
      customDropdownText={t('All Subjects')}
      initialSelectedId={initialSelectedSubject}
      isDisabled={assignmentsSubjectsCount < 2}
      testId="subjects-filter"
      ariaLabel="Select Subject"
    >
      <Dropdown.Item id={ALL_SUBJECTS}>{t('All Subjects')}</Dropdown.Item>
      {assignmentsSubjectsCount > 1
        ? assignmentsSubjects?.map((subject) => (
            <Dropdown.Item key={subject} id={subject}>
              {subject}
            </Dropdown.Item>
          ))
        : null}
    </Dropdown>
  );
};
